import { Injectable, OnDestroy } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  BACKEND_LABELS_KEY,
  BackendLabelOverrides,
  TenantLabelOverrideMap,
  TenantLabelOverrides,
  TenantLabelOverridesByLang,
} from '../../../models/i18n/tenant-label-overrides.model';

/** Location of the tenant override overlay, served as a static asset alongside the locale files. */
const TENANT_LABEL_OVERRIDES_URL = '/assets/i18n/tenant-label-overrides.json';

/** Fallback language used when ngx-translate has not settled on one yet. */
const FALLBACK_LANG = 'en';

/**
 * Precomputed lookup structures for one language's backend-label overrides.
 * Built once on load so runtime {@link TenantLabelOverrideService.convertTenantLabel}
 * calls are cheap map lookups instead of repeated normalization work.
 */
interface BackendLabelLookup {
  /** Exact source label -> display text (as authored, case-sensitive). */
  readonly exact: Map<string, string>;
  /** Lower-cased source label -> display text, for case-insensitive whole-string matches. */
  readonly lower: Map<string, string>;
  /**
   * `[lowerSource, target]` pairs sorted longest-first, for replacing a source
   * label embedded inside a longer string (e.g. "Email or License ID").
   */
  readonly phrases: ReadonlyArray<readonly [string, string]>;
}

/**
 * Overlays tenant-specific display text on top of the generic locale-file
 * translations without ever touching translation keys, component bindings, or
 * API payloads. Two independent mechanisms:
 *
 * 1. Translation-key overrides — merged into ngx-translate per language via
 *    setTranslation(lang, overrides, true) every time the active language
 *    finishes loading, so a key like `license ID` renders tenant wording.
 * 2. Backend-label overrides — {@link convertTenantLabel} relabels immutable,
 *    API-supplied strings (e.g. "License ID") that never pass through a
 *    translation key, using precomputed per-language lookup maps.
 *
 * Config is fetched once at bootstrap with `fetch` (not HttpClient, to avoid
 * interceptor/bootstrap cycles) and is best-effort: a missing or unreachable
 * file can never block app init or break translations.
 */
@Injectable({ providedIn: 'root' })
export class TenantLabelOverrideService implements OnDestroy {
  private overridesByLang: TenantLabelOverridesByLang = {};
  private backendLookups = new Map<string, BackendLabelLookup>();
  private hasBackendOverrides = false;
  private langChangeSub: Subscription | null = null;

  constructor(private readonly translate: TranslateService) {}

  /** True when this tenant has any backend-label transforms to apply. */
  get transformsActive(): boolean {
    return this.hasBackendOverrides;
  }

  /** Currently active ngx-translate language, falling back to the default/`en`. */
  get activeLang(): string {
    return (
      this.translate.currentLang ||
      this.translate.getDefaultLang() ||
      FALLBACK_LANG
    );
  }

  /**
   * Loads this tenant's label overrides and wires up re-application on every
   * language change. Intended to be awaited from an APP_INITIALIZER so overrides
   * are registered before the app renders.
   *
   * Best-effort: a missing/unreachable override file, a tenant with no entry, or
   * a malformed response are all caught and swallowed so app bootstrap is never
   * blocked and labels never render blank.
   */
  async loadAndApplyOverrides(): Promise<void> {
    // Subscribe before the fetch resolves: the real per-language translation file
    // load (triggered later by LanguageService) must complete first, or
    // setTranslation() here would make ngx-translate think the language is
    // already cached and it would skip loading the real file entirely.
    // onLangChange fires *after* that real load finishes, so subscribing early is
    // safe and guarantees we don't miss the first language switch.
    this.langChangeSub = this.translate.onLangChange.subscribe(
      (event: LangChangeEvent) => this.applyOverridesForLang(event.lang)
    );

    try {
      const response = await fetch(TENANT_LABEL_OVERRIDES_URL);
      if (!response.ok) {
        return;
      }
      const overrideMap = (await response.json()) as TenantLabelOverrideMap;
      this.ingestTenantBlock(overrideMap?.[environment.tenant_Name]);
    } catch {
      // Never throw: fall back to the generic locale-file text.
    }

    // A language may already be active by the time the fetch settles (e.g. the
    // locale file loaded first). Apply immediately so we don't wait for the next
    // switch that may never come.
    if (this.translate.currentLang) {
      this.applyOverridesForLang(this.translate.currentLang);
    }
  }

  ngOnDestroy(): void {
    this.langChangeSub?.unsubscribe();
  }

  /**
   * Relabels an immutable, API-supplied string for the active language using the
   * tenant's backend-label overrides. Cheap map lookups only. Returns the input
   * unchanged when there is no override (or no transforms for this tenant), so it
   * is safe to route every backend label through it.
   * @param text The exact label as supplied by the backend (e.g. "License ID").
   */
  convertTenantLabel(text: string): string {
    if (!this.hasBackendOverrides || !text) {
      return text;
    }
    // Prefer the active language; fall back to the fallback-language entry
    // (mirrors ngx-translate's own fallbackLang behavior) so a tenant that
    // only defines e.g. `en` still relabels when viewed in another language.
    const lookup =
      this.backendLookups.get(this.activeLang) ??
      this.backendLookups.get(FALLBACK_LANG);
    if (!lookup) {
      return text;
    }
    // Whole-string matches first — exact, then case-normalized.
    const exact = lookup.exact.get(text);
    if (exact !== undefined) {
      return exact;
    }
    const lower = lookup.lower.get(text.toLowerCase());
    if (lower !== undefined) {
      return lower;
    }
    // Longest-first phrase replacement for labels embedded in longer strings.
    return this.replaceEmbeddedPhrases(text, lookup.phrases);
  }

  /** Case-insensitively replaces each source phrase (longest-first) inside `text`. */
  private replaceEmbeddedPhrases(
    text: string,
    phrases: ReadonlyArray<readonly [string, string]>
  ): string {
    let result = text;
    let lowerResult = text.toLowerCase();
    for (const [source, target] of phrases) {
      let index = lowerResult.indexOf(source);
      while (index !== -1) {
        result = result.slice(0, index) + target + result.slice(index + source.length);
        lowerResult =
          lowerResult.slice(0, index) +
          target.toLowerCase() +
          lowerResult.slice(index + source.length);
        index = lowerResult.indexOf(source, index + target.length);
      }
    }
    return result;
  }

  /** Splits a raw tenant block into key overrides (per lang) and backend-label lookups. */
  private ingestTenantBlock(block: TenantLabelOverridesByLang | undefined): void {
    this.overridesByLang = block ?? {};
    const backendLabels = block?.[BACKEND_LABELS_KEY] as
      | BackendLabelOverrides
      | undefined;
    if (backendLabels) {
      this.buildBackendLookups(backendLabels);
    }
  }

  /** Precomputes per-language lookup maps from the raw backendLabels config. */
  private buildBackendLookups(backendLabels: BackendLabelOverrides): void {
    // Pivot { sourceLabel: { lang: target } } into { lang: [sourceLabel, target][] }.
    const byLang = new Map<string, Array<[string, string]>>();
    for (const [source, byLangMap] of Object.entries(backendLabels)) {
      for (const [lang, target] of Object.entries(byLangMap)) {
        const entries = byLang.get(lang) ?? [];
        entries.push([source, target]);
        byLang.set(lang, entries);
      }
    }

    this.backendLookups = new Map();
    for (const [lang, entries] of byLang) {
      const exact = new Map<string, string>();
      const lower = new Map<string, string>();
      for (const [source, target] of entries) {
        exact.set(source, target);
        lower.set(source.toLowerCase(), target);
      }
      const phrases = entries
        .map(([source, target]) => [source.toLowerCase(), target] as const)
        .sort((a, b) => b[0].length - a[0].length);
      this.backendLookups.set(lang, { exact, lower, phrases });
    }
    this.hasBackendOverrides = this.backendLookups.size > 0;
  }

  /**
   * Deep-merges this tenant's translation-key overrides for the given language on
   * top of the already-loaded locale-file translations. No-ops when there is
   * nothing to merge.
   * @param lang ngx-translate language code that just finished loading.
   */
  private applyOverridesForLang(lang: string): void {
    const overrides = this.overridesByLang[lang] as TenantLabelOverrides | undefined;
    if (!overrides || Object.keys(overrides).length === 0) {
      return;
    }
    // `true` performs a deep merge of only these keys on top of the existing
    // language data — it must NOT replace the whole language object, or every
    // other translation key for this language would disappear.
    this.translate.setTranslation(lang, overrides, true);
  }
}
